import BaseRepository from "~/api/BaseRepository";
import type { CompanyStore } from "~/stores/companyStore";
import type {$Fetch} from "nitropack";

export interface CompanyContact {
    first_name?: string;
    last_name?: string;
    email?: string;
}

export interface CompanyPayload {
    company_id?: string;
    business_name?: string;
    public_name?: string;
    phone?: string;
    address?: string;
    city?: string;
    postal_code?: string;
    siren?: string;
    siret: string;
    ape_code?: string;
    intra_community_vat?: string;
    subject_vat: boolean;
    legal_form_id?: string;
    invoice_ninja_company_id?: string;
    contact: CompanyContact;
    tenant_id?: string;
}

export interface StripeAccountUrlResponse {
    account_link_url: string;
}

export interface StripeAccountStatusResponse {
    is_enabled: boolean;
}

export interface StripeCustomerResponse {
    customer_id: string;
}

export interface ListCompaniesQueryParams {
    limit?: number;
    offset?: number;
    me?: boolean;
}

export interface ListCompaniesResponse {
    companies: CompanyStore[];
    total: number;
}

export interface SearchCompaniesResponse {
    companies: CompanyStore[];
    limit: number;
    offset: number;
    total: number;
}

export const CompanyRepository = (client: $Fetch) => ({
    ...BaseRepository(client)('/v1/companies'),
    
    patchCompany: (payload: Partial<CompanyStore>, config: any = {}) => {
        const formData = new FormData();
        const companyId = payload.company_id; // Extract company_id from payload

        if (!companyId) {
            throw new Error('Company ID is required for patching.');
        }

        // Remove company_id from payload before appending to formData, as it's in the path
        const { company_id, ...restPayload } = payload;

        Object.entries(restPayload).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value.toString());
            }
        });

        return client(`/v1/companies/${companyId}`, {
            method: 'PATCH',
            body: formData,
            ...config,
        });
    },
    
    getLegalForms: () => {
        return client(`/v1/companies/legal-forms`, {
            method: 'GET',
        });
    },
    
    createCompany: (payload: CompanyPayload, config: any = {}) => {
        return client(`/v1/companies`, {
            method: 'POST',
            body: payload,
            headers: {
                "Content-Type": "application/json",
            },
            ...config,
        }) as Promise<CompanyStore>;
    },
    
    createStripeAccount: (companyId: string) => {
        return client(`/v1/companies/${companyId}/stripe/account`, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
            }
        }) as Promise<void>;
    },
    
    createStripeCustomer: (companyId: string) => {
        return client(`/v1/companies/${companyId}/stripe/customer`, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
            }
        }) as Promise<StripeCustomerResponse>;
    },
    
    getStripeAccountUrl: (companyId: string, returnUrl?: string) => {
        // Append return_url as query parameter if provided
        const queryParams = new URLSearchParams();
        if (returnUrl) {
            queryParams.append('return_url', returnUrl);
        }
        const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
        
        return client(`/v1/companies/${companyId}/stripe/account-url${queryString}`, {
            method: 'GET',
        }) as Promise<StripeAccountUrlResponse>;
    },
    
    getStripeAccountStatus: (companyId: string) => {
        return client(`/v1/companies/${companyId}/stripe/account-status`, {
            method: 'GET',
        }) as Promise<StripeAccountStatusResponse>;
    },
    
    fetchAllCompanies: (params: ListCompaniesQueryParams = {}) => {
        // Build query parameters
        const queryParams = new URLSearchParams();

        if (params.limit) queryParams.append('limit', params.limit.toString());
        if (params.offset) queryParams.append('offset', params.offset.toString());
        if (params.me) queryParams.append('me', 'true');

        const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

        return client(`/v1/companies${queryString}`, {
            method: 'GET'
        }) as Promise<ListCompaniesResponse>;
    },

    searchCompanies: (q: string, source: 'external' | 'internal' = 'internal', params: any = {}) => {
        // Build query parameters
        const queryParams = new URLSearchParams();

        queryParams.append('source', source.toString());
        queryParams.append('q', q.toString());

        if (params && typeof params === 'object') {
            Object.keys(params).forEach(item => {
                queryParams.append(item, params[item]);
            })
        }

        const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

        return client(`/v1/companies/search${queryString}`, {
            method: 'GET',
        }) as Promise<SearchCompaniesResponse>;
    },

    fetchCompany: (companyId: string) => {
        return client(`/v1/companies/${companyId}`, {
            method: 'GET'
        }) as Promise<CompanyStore>;
    }
})